import React from 'react';
import { UserContext } from '../../../state';
import toast from 'react-hot-toast';
import { CREATE_DEVICE_CODE, GET_DEVICE_CODE, REQUEST_ADDITIONAL_CODE } from '../../../dal/ttLock';
import { format, parse } from 'date-fns';

function getDistanceAndCheckProximity(lat1, lng1, lat2, lng2, threshold = 200) {
  const toRadians = (degrees) => (degrees * Math.PI) / 180;

  const earthRadiusMeters = 6371000; // Earth's radius in meters

  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const distance = earthRadiusMeters * c;

  return {
    isWithinThreshold: distance <= threshold,
    distance: distance
  };
}

// Locks a tour opens: the unit's door lock, then its building's entrance lock. Older servers
// only send the single `ttLockDevice`.
export const getTourLocks = (location) => {
  const list = location?.ttLockDevices;
  if (Array.isArray(list) && list.length) return list.filter((lock) => lock?.lockId);
  return location?.ttLockDevice?.lockId ? [location.ttLockDevice] : [];
};

export const lockDoorLabel = (lock) =>
  lock?.locationType === 'building' ? 'Building entrance' : 'Unit door';

export const useGenerateCode = ({ showing }) => {
  const { userContext } = React.useContext(UserContext);
  // one { lock, code } per tour lock
  const [accessCodes, setAccessCodes] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isRequestingAdditional, setIsRequestingAdditional] = React.useState(false);
  const [additionalCodes, setAdditionalCodes] = React.useState([]);
  const [errorMessage, setErrorMessage] = React.useState(null);
  // doors whose code (or additional code) failed: [{ lock, reason }] — retried on their own
  const [failedDoors, setFailedDoors] = React.useState([]);
  const [additionalFailedDoors, setAdditionalFailedDoors] = React.useState([]);

  const tourLocks = React.useMemo(() => getTourLocks(showing?.location), [showing]);
  const isMultiLock = tourLocks.length > 1;
  const pendingLocks = React.useRef(tourLocks);

  // Runs `request(lock)` for each door one after another; a failed door never blocks or discards
  // another door's result. Resolves with { done: [{ lock, code }], failed: [{ lock, reason }] }.
  const perDoor = (locks, request) =>
    locks.reduce(
      (chain, lock) =>
        chain.then((outcome) =>
          request(lock)
            .then((code) =>
              code && !code.error
                ? { ...outcome, done: [...outcome.done, { lock, code }] }
                : { ...outcome, failed: [...outcome.failed, { lock, reason: code?.error }] }
            )
            .catch((err) => ({
              ...outcome,
              failed: [...outcome.failed, { lock, reason: err?.reason || err?.message }]
            }))
        ),
      Promise.resolve({ done: [], failed: [] })
    );

  // `locks` defaults to every tour door; a retry passes only the doors that failed
  const requestAdditionalCode = (reason, locks = tourLocks) => {
    setIsRequestingAdditional(true);
    return perDoor(locks, (lock) =>
      REQUEST_ADDITIONAL_CODE({
        userContext,
        data: {
          appId: showing?._id,
          portalUserId: userContext?._id,
          companyId: userContext?.companyId,
          lockId: Number(lock.lockId),
          reason
        }
      })
    )
      .then(({ done, failed }) => {
        setAdditionalCodes((current) => [...current, ...done]);
        setAdditionalFailedDoors(failed);
        if (failed.length && !done.length && !isMultiLock) {
          toast.error(failed[0].reason || 'Failed to get additional code. Please try again.');
        }
      })
      .finally(() => {
        setIsRequestingAdditional(false);
      });
  };

  // `locks` defaults to every tour door; a retry passes only the doors that failed
  const generateCode = (locks = tourLocks) => {
    pendingLocks.current = locks;
    setIsLoading(true);
    if (!navigator.geolocation) {
      console.log('Geolocation is not supported by this browser.');
      return setIsLoading(false);
    }

    navigator.geolocation.getCurrentPosition(handleGeolocationSuccess, handleGeolocationError);
  };

  const handleGeolocationSuccess = (position) => {
    if (!isValidPosition(position)) {
      toast.error('Geolocation not valid.');
      return setIsLoading(false);
    }

    const comparedCoords = getDistanceAndCheckProximity(
      position.coords.latitude,
      position.coords.longitude,
      showing.location.address.geopoint[1],
      showing.location.address.geopoint[0]
    );

    if (!comparedCoords?.isWithinThreshold) return setIsLoading(false);

    // sequential — each lock's code is issued by TTLock separately
    perDoor(pendingLocks.current, getOrCreateCode)
      .then(({ done, failed }) => {
        setAccessCodes((current) => [...current, ...done]);
        setFailedDoors(failed);
        // single door (or every door failed): show the reason where the code would be
        setErrorMessage(failed.length && !done.length ? failed[0].reason || null : null);
        if (failed.length && !done.length && !failed[0].reason)
          toast.error('Failed to get the code.');
      })
      .finally(() => setIsLoading(false));
  };

  const handleGeolocationError = (error) => {
    console.error('Error getting location:', error);
    toast.error('Location services not enabled.');
    setIsLoading(false);
  };

  const isValidPosition = (position) => {
    return (
      position.coords.latitude &&
      showing?.location?.address?.geopoint?.length > 0 &&
      tourLocks.length > 0
    );
  };

  // an existing valid code for this lock, else a new one
  const getOrCreateCode = (lock) =>
    GET_DEVICE_CODE({
      userContext,
      data: {
        appId: showing?._id,
        portalUserId: userContext?._id,
        companyId: userContext?.companyId,
        // only ask per lock when there are several — keeps the single-lock request unchanged
        ...(isMultiLock ? { lockId: lock.lockId } : {})
      }
    }).then((existing) => existing || createDeviceCode(lock));

  const createDeviceCode = (lock) => {
    const formatString = 'MM/dd/yyyy hh:00 a';
    const currentDate = new Date();
    const formattedDateString = format(currentDate, formatString);

    // Parse the formatted date string back into a Date object
    const parsedDate = parse(formattedDateString, formatString, new Date());

    // Convert the Date object to Unix timestamp (in seconds)
    const unixTimestamp = parsedDate.getTime();

    return CREATE_DEVICE_CODE({
      userContext,
      data: {
        appId: showing?._id,
        portalUserId: userContext?._id,
        companyId: userContext?.companyId,
        lockId: Number(lock?.lockId),
        userId: lock?.ownerId,
        startDate: unixTimestamp
      }
    }).then((resp) => {
      // reported per door by perDoor
      if (resp?.error) return { error: resp.error };
      if (resp?.startDate) {
        resp.startDate = new Date(resp.startDate).toISOString();
        resp.endDate = new Date(resp.endDate).toISOString();
      }
      return resp;
    });
  };

  return {
    generateCode,
    accessCodes,
    accessCode: accessCodes[0]?.code || '',
    isMultiLock,
    isLoading,
    requestAdditionalCode,
    isRequestingAdditional,
    additionalCodes,
    additionalCode: additionalCodes[0]?.code || null,
    failedDoors,
    additionalFailedDoors,
    retryFailedDoors: () => generateCode(failedDoors.map(({ lock }) => lock)),
    retryAdditionalFailedDoors: (reason) =>
      requestAdditionalCode(
        reason,
        additionalFailedDoors.map(({ lock }) => lock)
      ),
    errorMessage,
    clearError: () => setErrorMessage(null)
  };
};
